import React from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area 
} from 'recharts';
import { Box, Typography, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Remove } from '@mui/icons-material';

const WeeklyCostChart = ({ data, topDishes = [] }) => {
  const { t } = useTranslation();

  // Mock weekly cost data - in real app this would come from receipt scanning or manual input
  const generateWeeklyCostData = () => {
    const weeks = [];
    const currentDate = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const weekDate = new Date(currentDate);
      weekDate.setDate(weekDate.getDate() - (i * 7));
      
      // Generate realistic grocery costs based on user activity
      const baseWeeklyCost = 80; // Base weekly grocery cost
      const variation = Math.random() * 40 - 20; // +/- $20 variation
      const activityMultiplier = data.cookedDishes > 0 ? 1 + (data.cookedDishes / 100) : 0.8;
      
      const weeklyCost = Math.max(30, baseWeeklyCost + variation) * activityMultiplier;
      
      weeks.push({
        week: `${weekDate.getMonth() + 1}/${weekDate.getDate()}`,
        cost: Math.round(weeklyCost * 100) / 100,
        meals: Math.floor(Math.random() * 8) + 5, // 5-12 meals per week
        date: weekDate
      });
    }
    
    return weeks;
  };

  const weeklyData = generateWeeklyCostData();
  
  // Calculate trends and insights
  const totalSpent = weeklyData.reduce((sum, week) => sum + week.cost, 0);
  const averageWeekly = totalSpent / weeklyData.length;
  const lastWeek = weeklyData[weeklyData.length - 1];
  const previousWeek = weeklyData[weeklyData.length - 2];
  const costTrend = lastWeek.cost > previousWeek.cost ? 'up' : lastWeek.cost < previousWeek.cost ? 'down' : 'same';
  const trendPercentage = ((lastWeek.cost - previousWeek.cost) / previousWeek.cost * 100).toFixed(1);
  
  const getTrendIcon = () => {
    switch (costTrend) {
      case 'up': return <TrendingUp sx={{ fontSize: 16, color: 'error.main' }} />;
      case 'down': return <TrendingDown sx={{ fontSize: 16, color: 'success.main' }} />;
      default: return <Remove sx={{ fontSize: 16, color: 'grey.500' }} />;
    }
  };

  const getTrendColor = () => {
    switch (costTrend) {
      case 'up': return 'error';
      case 'down': return 'success';
      default: return 'default';
    }
  };

  if (!weeklyData.length) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          height: 300,
          color: 'text.secondary'
        }}
      >
        <Typography variant="body2">
          No grocery cost data available yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 300 }}>
      {/* Cost Summary */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip 
          label={`Avg: $${averageWeekly.toFixed(0)}/week`}
          color="primary"
          variant="outlined"
        />
        <Chip 
          label={`Last week: $${lastWeek.cost.toFixed(0)}`}
          color={getTrendColor()}
          variant="filled"
          icon={getTrendIcon()}
        />
        {costTrend !== 'same' && (
          <Chip 
            label={`${costTrend === 'up' ? '+' : ''}${trendPercentage}%`}
            color={getTrendColor()}
            size="small"
            variant="outlined"
          />
        )}
      </Box>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart
          data={weeklyData}
          margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 0,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis 
            dataKey="week" 
            fontSize={12}
            tickMargin={5}
          />
          <YAxis 
            fontSize={12}
            tickFormatter={(value) => `$${value}`}
          />
          <Tooltip 
            formatter={(value, name) => [`$${value.toFixed(2)}`, 'Weekly Cost']}
            labelFormatter={(label, payload) => {
              if (payload && payload.length > 0) {
                const data = payload[0].payload;
                return `Week of ${label} • ${data.meals} meals planned`;
              }
              return `Week of ${label}`;
            }}
            contentStyle={{ 
              backgroundColor: '#f5f5f5', 
              border: '1px solid #ccc',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
          />
          <Area
            type="monotone"
            dataKey="cost"
            stroke="#1976d2"
            strokeWidth={2}
            fill="url(#colorCost)"
            fillOpacity={0.6}
          />
          <defs>
            <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#1976d2" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default WeeklyCostChart;